// Web floating format toolbar — a "bubble" that portals above the
// current text selection, mirroring the Element/Matrix lexical
// composer's `FloatingRichTextToolbar`. It mounts ONLY when the user
// has a non-collapsed text selection inside the editor root (or while a
// toolbar-owned popover is open), settles after the selection stops
// moving so it doesn't flicker mid-drag, and repositions on
// scroll/resize/selectionchange.
//
// The bar's visible content is the shared RN `<FormatOptions />` passed
// as `children`; this component only owns visibility + positioning. It
// renders through `WebPortal` (→ document.body) so it escapes the
// composer's overflow / stacking context.

import React, {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { WebPortal } from "./WebPortal";

/** Viewport edge padding so the bar never sits flush to the window. */
const VIEW_MARGIN = 8;
/** Gap between the selection rect and the bar. */
const GAP = 6;
/** After keyboard / programmatic selection changes, wait before showing. */
const SELECTION_SETTLE_MS = 220;
/** After pointer release with a selection, reveal a little faster. */
const POINTER_UP_SETTLE_MS = 60;
/** Marker attribute used to detect focus landing inside the bar. */
const TOOLBAR_MARKER = "data-ping-floating-toolbar";

export interface FloatingFormatToolbarProps {
  readonly editorRootRef: RefObject<HTMLElement | null>;
  readonly disabled?: boolean;
  readonly popoverOpen?: boolean;
  readonly children: React.ReactNode;
  readonly testID?: string;
}

/** Non-collapsed DOM selection fully inside the editor root. */
function hasNonCollapsedTextSelectionInRoot(root: HTMLElement | null): boolean {
  if (!root || typeof window === "undefined") return false;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;
  return !range.collapsed;
}

function shouldMount(
  disabled: boolean,
  popoverOpen: boolean,
  root: HTMLElement | null,
  selectionSettled: boolean,
): boolean {
  if (disabled) return false;
  if (popoverOpen) return true;
  if (typeof document === "undefined" || !root) return false;
  if (document.activeElement?.closest(`[${TOOLBAR_MARKER}]`)) return true;
  return hasNonCollapsedTextSelectionInRoot(root) && selectionSettled;
}

function getSelectionRectInRoot(root: HTMLElement): DOMRect | null {
  if (typeof window === "undefined") return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  let rect = range.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    const clientRects = Array.from(range.getClientRects()).filter(
      (r) => r.height > 0 || r.width > 0,
    );
    if (clientRects.length > 0) rect = clientRects[0]!;
  }
  if (
    rect.width === 0 &&
    rect.height === 0 &&
    range.startContainer.nodeType === Node.TEXT_NODE
  ) {
    const el = (range.startContainer as Text).parentElement;
    if (el && root.contains(el)) rect = el.getBoundingClientRect();
  }
  if (rect.width === 0 && rect.height === 0) return null;
  return rect;
}

export function FloatingFormatToolbar({
  editorRootRef,
  disabled = false,
  popoverOpen = false,
  children,
  testID,
}: FloatingFormatToolbarProps): React.ReactElement | null {
  const shellRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const popoverOpenRef = useRef(popoverOpen);
  popoverOpenRef.current = popoverOpen;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const [selectionSettled, setSelectionSettled] = useState(false);
  const [fadeVisible, setFadeVisible] = useState(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerDownInEditorRef = useRef(false);
  const prevMountedRef = useRef(false);

  // Re-run the visibility check when the selection moves (the parent
  // may not re-render).
  const [, bumpSelectionRevision] = useReducer((n: number) => n + 1, 0);

  const clearSettleTimer = useCallback((): void => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const scheduleSelectionSettled = useCallback(
    (delayMs: number): void => {
      clearSettleTimer();
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        const root = editorRootRef.current;
        if (
          popoverOpenRef.current ||
          hasNonCollapsedTextSelectionInRoot(root)
        ) {
          setSelectionSettled(true);
        } else {
          setSelectionSettled(false);
        }
        bumpSelectionRevision();
      }, delayMs);
    },
    [clearSettleTimer, editorRootRef],
  );

  useEffect(() => {
    if (popoverOpen) {
      clearSettleTimer();
      setSelectionSettled(true);
    }
  }, [popoverOpen, clearSettleTimer]);

  const recomputePosition = useCallback((): void => {
    const root = editorRootRef.current;
    if (!root || disabled) {
      setPos(null);
      return;
    }
    if (!shouldMount(disabled, popoverOpen, root, selectionSettled)) {
      setPos(null);
      return;
    }
    const rect = getSelectionRectInRoot(root);
    const shell = shellRef.current;
    const shellH = shell?.offsetHeight ?? 40;
    const shellW = shell?.offsetWidth ?? 336;

    const anchor = rect ?? root.getBoundingClientRect();
    const centerX = anchor.left + anchor.width / 2;
    let top = anchor.top - GAP - shellH;
    if (top < VIEW_MARGIN) top = anchor.bottom + GAP;
    const clampedCenterX = Math.min(
      Math.max(centerX, VIEW_MARGIN + shellW / 2),
      (typeof window !== "undefined" ? window.innerWidth : 1024) -
        VIEW_MARGIN -
        shellW / 2,
    );
    const maxTop =
      (typeof window !== "undefined" ? window.innerHeight : 768) -
      VIEW_MARGIN -
      shellH;
    top = Math.min(Math.max(top, VIEW_MARGIN), maxTop);
    setPos({ left: clampedCenterX, top });
  }, [disabled, editorRootRef, popoverOpen, selectionSettled]);

  useLayoutEffect(() => {
    recomputePosition();
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(recomputePosition);
    });
    return () => window.cancelAnimationFrame(id);
  }, [recomputePosition, popoverOpen, selectionSettled, children]);

  useEffect(() => {
    const root = editorRootRef.current;
    if (!root || disabled) return;

    const onSelectionChange = (): void => {
      bumpSelectionRevision();
      const r = editorRootRef.current;
      clearSettleTimer();
      if (!r) {
        setSelectionSettled(false);
        window.requestAnimationFrame(recomputePosition);
        return;
      }
      if (popoverOpenRef.current) {
        setSelectionSettled(true);
        window.requestAnimationFrame(recomputePosition);
        return;
      }
      if (!hasNonCollapsedTextSelectionInRoot(r)) {
        setSelectionSettled(false);
        window.requestAnimationFrame(recomputePosition);
        return;
      }
      if (pointerDownInEditorRef.current) {
        setSelectionSettled(false);
        window.requestAnimationFrame(recomputePosition);
        return;
      }
      scheduleSelectionSettled(SELECTION_SETTLE_MS);
      window.requestAnimationFrame(recomputePosition);
    };

    const onPointerDown = (e: PointerEvent): void => {
      if (!root.contains(e.target as Node)) return;
      pointerDownInEditorRef.current = true;
      if (!popoverOpenRef.current) {
        setSelectionSettled(false);
        clearSettleTimer();
      }
    };

    const onGlobalPointerUp = (): void => {
      const was = pointerDownInEditorRef.current;
      pointerDownInEditorRef.current = false;
      if (!was || popoverOpenRef.current || disabledRef.current) return;
      const r = editorRootRef.current;
      if (r && hasNonCollapsedTextSelectionInRoot(r)) {
        scheduleSelectionSettled(POINTER_UP_SETTLE_MS);
      }
    };

    const onScrollOrResize = (): void => {
      window.requestAnimationFrame(recomputePosition);
    };

    root.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onGlobalPointerUp);
    window.addEventListener("pointercancel", onGlobalPointerUp);
    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onGlobalPointerUp);
      window.removeEventListener("pointercancel", onGlobalPointerUp);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      clearSettleTimer();
    };
  }, [
    editorRootRef,
    disabled,
    recomputePosition,
    clearSettleTimer,
    scheduleSelectionSettled,
  ]);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(recomputePosition);
    });
    ro.observe(shell);
    return () => ro.disconnect();
  }, [recomputePosition]);

  const mounted = shouldMount(
    disabled,
    popoverOpen,
    editorRootRef.current,
    selectionSettled,
  );

  // Fade in on first mount of a given selection; reset when unmounted.
  useLayoutEffect(() => {
    if (!mounted) {
      setFadeVisible(false);
      prevMountedRef.current = false;
      return;
    }
    if (!prevMountedRef.current) {
      setFadeVisible(false);
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setFadeVisible(true));
      });
      prevMountedRef.current = true;
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [mounted]);

  if (!mounted) return null;

  return (
    <WebPortal>
      <div
        ref={shellRef}
        {...{ [TOOLBAR_MARKER]: "" }}
        data-testid={testID}
        role="toolbar"
        style={{
          position: "fixed",
          left: pos ? pos.left : -9999,
          top: pos ? pos.top : -9999,
          transform: "translateX(-50%)",
          visibility: pos ? "visible" : "hidden",
          opacity: fadeVisible ? 1 : 0,
          transition: "opacity 120ms ease",
          zIndex: 10050,
        }}
        onMouseDown={(e) => {
          // Keep the editor's selection alive when the user clicks the
          // bar's chrome (padding / separators). Individual chips run
          // their own `preventDefault` via FormatOptions' focus guard;
          // this covers clicks that land on the bar background.
          const target = e.target as HTMLElement | null;
          if (
            target?.closest(
              "input, textarea, select, [contenteditable=true]",
            )
          ) {
            return;
          }
          e.preventDefault();
        }}
      >
        {children}
      </div>
    </WebPortal>
  );
}
